import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CalendarDays, CalendarPlus, Hammer, Laptop, LifeBuoy, Wrench } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { AssetStatusBadge } from '../components/badges';
import { ModelImage } from '../components/model-image';
import { PendingSurveysAlert } from '../components/ticket-tracking';
import { WorkOrderStatusBadge } from '../components/work-order-badges';
import { WorkOrderFormModal } from '../components/WorkOrderFormModal';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, PageHeader, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DeskProvider, workOrderHref } from '../lib/desk';
import { eventPlaceLabel, TEAM_LABEL, type EventRequestPage } from '../lib/events';
import { equipmentLabel, placeLabel } from '../lib/facilities';
import { formatDateTime, formatRelative } from '../lib/format';
import type { MyDevices, Page, ReportableAsset, WorkOrder } from '../lib/types';
import { REPORT_REASON_LABEL } from '../lib/work-orders';

/** How many of each kind of request the home page lists. */
const RECENT = 5;

const viewAll = (to: string, total: number | undefined) => (
  <Link to={to} className="text-xs font-medium whitespace-nowrap text-brand-700 hover:underline">
    {total && total > RECENT ? `View all ${total}` : 'View all'}
  </Link>
);

/** A list's loading, error, and empty states, or the list. */
function SectionBody<T>({ query, empty, children }: { query: UseQueryResult<T>; empty: (data: T) => ReactNode; children: (data: T) => ReactNode }) {
  if (query.isLoading) {
    return (
      <div className="flex h-28 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return empty(query.data) ?? children(query.data);
}

/** The requester portal's front page: their devices beside their technology, event, and facilities requests. */
export function RequesterHomePage() {
  const { me, canFacilities } = useAuth();
  const navigate = useNavigate();
  // null: form closed. { device: null }: choose the device in the form.
  const [reporting, setReporting] = useState<{ device: ReportableAsset | null } | null>(null);
  const [requestingWork, setRequestingWork] = useState(false);
  const facilities = !!me?.tenant?.facilitiesEnabled && canFacilities('REQUESTER');
  const firstName = me?.user.name.trim().split(/\s+/)[0];

  return (
    <>
      <PageHeader
        title={firstName ? `Hi, ${firstName}` : 'Home'}
        description="Your devices and everything you’ve asked for, in one place."
        actions={
          <Button variant="primary" icon={<Wrench />} onClick={() => setReporting({ device: null })}>
            Report a problem
          </Button>
        }
      />

      <PendingSurveysAlert className="mb-6" />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <MyDevices onReport={(device) => setReporting({ device })} />
        <div className="min-w-0 space-y-6">
          <TechRequests onReport={() => setReporting({ device: null })} />
          <MyEvents onRequest={() => navigate('/events/new')} />
          {facilities && <FacilityRequests onRequest={() => setRequestingWork(true)} />}
        </div>
      </div>

      <WorkOrderFormModal open={!!reporting} onClose={() => setReporting(null)} device={reporting?.device ?? null} />
      {facilities && (
        <DeskProvider service="FACILITIES">
          <WorkOrderFormModal open={requestingWork} onClose={() => setRequestingWork(false)} onCreated={(ticket) => navigate(workOrderHref('FACILITIES', ticket.id))} />
        </DeskProvider>
      )}
    </>
  );
}

function MyDevices({ onReport }: { onReport(device: ReportableAsset): void }) {
  const { me } = useAuth();
  const devices = useQuery({ queryKey: ['my-devices'], queryFn: () => api.get<MyDevices>('/my/devices') });
  const rooms = devices.data?.rooms ?? [];

  return (
    <Card>
      <CardHeader
        title="My devices"
        description={rooms.length ? `Assigned to you, or to or kept in ${rooms.map((room) => room.name).join(', ')}` : 'Devices assigned to you'}
      />
      <SectionBody
        query={devices}
        empty={(data) =>
          !data.person ? (
            <div className="p-4">
              <Alert tone="amber" title="We couldn’t find your devices">
                Your sign-in email ({me?.user.email}) doesn’t match an active person at {me?.tenant?.name}. Ask the technology office to check it. You can
                still report a problem using the asset tag on the device’s label.
              </Alert>
            </div>
          ) : data.assets.length === 0 ? (
            <EmptyState icon={<Laptop />} title="No devices to show" description="Nothing is checked out to you or your rooms." className="py-10" />
          ) : null
        }
      >
        {(data) => (
          <ul className="divide-y divide-slate-100">
            {data.assets.map((device) => (
              <li key={device.id} className="flex gap-3 px-4 py-3">
                <ModelImage url={device.imageUrl} alt={device.model ?? ''} className="size-16" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{device.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    <span className="font-mono">{device.assetTag}</span>
                    {device.model && ` · ${device.model}`}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge tone="brand">
                      {REPORT_REASON_LABEL[device.reason]}
                      {device.roomName && ` · ${device.roomName}`}
                    </Badge>
                    {device.status !== 'CHECKED_OUT' && device.status !== 'AVAILABLE' && <AssetStatusBadge status={device.status} />}
                  </div>
                  {device.workOrders.map((ticket) => (
                    <Link key={ticket.id} to={workOrderHref('IT', ticket.id)} className="mt-1 flex items-center gap-1.5 text-xs text-brand-700 hover:underline">
                      <span className="truncate">
                        #{ticket.number} {ticket.title}
                      </span>
                      <WorkOrderStatusBadge status={ticket.status} />
                    </Link>
                  ))}
                  <button type="button" onClick={() => onReport(device)} className="mt-1.5 text-xs font-medium text-brand-700 hover:underline">
                    Report a problem
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionBody>
    </Card>
  );
}

function TechRequests({ onReport }: { onReport(): void }) {
  const tickets = useQuery({
    queryKey: ['work-orders', 'mine', 'IT', 'home'],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', { mine: true, state: 'all', pageSize: RECENT }),
  });

  return (
    <Card>
      <CardHeader
        title="My tech requests"
        description="Device and technology problems you reported, or that were reported for you"
        actions={
          <>
            {viewAll('/requests', tickets.data?.total)}
            <Button size="sm" icon={<Wrench />} onClick={onReport}>
              Report a problem
            </Button>
          </>
        }
      />
      <SectionBody
        query={tickets}
        empty={(data) =>
          data.items.length === 0 ? <EmptyState icon={<LifeBuoy />} title="No tech requests yet" description="When a device acts up, report it here." className="py-10" /> : null
        }
      >
        {(data) => (
          <ul className="divide-y divide-slate-100">
            {data.items.map((ticket) => (
              <li key={ticket.id}>
                <Link to={workOrderHref('IT', ticket.id)} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/80">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {ticket.asset ? `${ticket.asset.assetTag} · ${ticket.asset.name}` : (ticket.software?.name ?? 'No device')} · Updated {formatRelative(ticket.updatedAt)}
                      {ticket.assignedTo && ` · ${ticket.assignedTo.name}`}
                    </p>
                  </div>
                  <WorkOrderStatusBadge status={ticket.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SectionBody>
    </Card>
  );
}

function MyEvents({ onRequest }: { onRequest(): void }) {
  const events = useQuery({
    queryKey: ['event-requests', 'home'],
    queryFn: () => api.get<EventRequestPage>('/event-requests', { when: 'upcoming', scope: 'mine', pageSize: RECENT }),
  });

  return (
    <Card>
      <CardHeader
        title="My events"
        description="Upcoming events you asked to have set up"
        actions={
          <>
            {viewAll('/events', events.data?.total)}
            <Button size="sm" icon={<CalendarPlus />} onClick={onRequest}>
              Request an event
            </Button>
          </>
        }
      />
      <SectionBody
        query={events}
        empty={(data) =>
          data.items.length === 0 ? (
            <EmptyState icon={<CalendarDays />} title="No upcoming events" description="Ask for an event setup and both teams get the details." className="py-10" />
          ) : null
        }
      >
        {(data) => (
          <ul className="divide-y divide-slate-100">
            {data.items.map((event) => (
              <li key={event.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3">
                <Link to={`/events/${event.id}`} className="min-w-0 flex-1 basis-60">
                  <p className="truncate text-sm font-medium text-slate-900 hover:text-brand-700">{event.title}</p>
                  <p className="truncate text-xs text-slate-500">
                    {formatDateTime(event.startsAt)} · {eventPlaceLabel(event)}
                  </p>
                </Link>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {event.workOrders.map((ticket) => (
                    <Link key={ticket.id} to={workOrderHref(ticket.service, ticket.id)} className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
                      <span className="text-slate-500">{TEAM_LABEL[ticket.service]}</span>
                      <WorkOrderStatusBadge status={ticket.status} />
                    </Link>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionBody>
    </Card>
  );
}

function FacilityRequests({ onRequest }: { onRequest(): void }) {
  const tickets = useQuery({
    queryKey: ['work-orders', 'mine', 'FACILITIES', 'home'],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', { service: 'FACILITIES', mine: true, state: 'all', pageSize: RECENT }),
  });

  return (
    <Card>
      <CardHeader
        title="My facility requests"
        description="Repairs, cleaning, and other work you asked the facilities team for"
        actions={
          <>
            {viewAll('/facilities/requests', tickets.data?.total)}
            <Button size="sm" icon={<Hammer />} onClick={onRequest}>
              Request work
            </Button>
          </>
        }
      />
      <SectionBody
        query={tickets}
        empty={(data) =>
          data.items.length === 0 ? (
            <EmptyState icon={<Hammer />} title="No facility requests yet" description="When something needs fixing, cleaning, or checking, ask here." className="py-10" />
          ) : null
        }
      >
        {(data) => (
          <ul className="divide-y divide-slate-100">
            {data.items.map((ticket) => (
              <li key={ticket.id}>
                <Link to={workOrderHref('FACILITIES', ticket.id)} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/80">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {[
                        placeLabel({ building: ticket.building ?? ticket.room?.building, room: ticket.room, locationDetail: ticket.locationDetail }),
                        ticket.facilityAsset && equipmentLabel(ticket.facilityAsset),
                        `Updated ${formatRelative(ticket.updatedAt)}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <WorkOrderStatusBadge status={ticket.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SectionBody>
    </Card>
  );
}
